import { kleur } from '../theme/tokens';
import { gedeeld } from '../theme/stijlen';
import React, { useState, useEffect, useRef } from 'react';
import { User, LogOut, ChevronLeft, ChevronRight, Plus, X, Check, Send, BookOpen } from 'lucide-react';
import { maakApi } from '../api/client';
import { Portret } from '../components/Portret';

// Hoe lang na de opening van Socrates een van de personages ongevraagd zijn
// eerste reactie geeft. Genoeg tijd om Socrates te lezen, kort genoeg om niet
// tegen een stilte aan te kijken.
const OPENING_NA_MS = 10000;

function StudentFlow({ user, token, onLogout, apiUrl }) {
  const [step, setStep] = useState('loading');
  const [session, setSession] = useState(null);
  const [discussion, setDiscussion] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [discussieleider, setDiscussieleider] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [activePersona, setActivePersona] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groepjeNaam, setGroepjeNaam] = useState('');
  const [groepjeAvatar, setGroepjeAvatar] = useState('🦊');
  // Groepsgenoten werken onder dit account mee. Per genoot bewaren we het
  // ondertekende bewijs uit /student/groepslid; dat gaat mee bij het aanmaken
  // van het groepje. Er staat nooit een wachtwoord in deze state.
  const [groepsgenoten, setGroepsgenoten] = useState([]);
  const [genootForm, setGenootForm] = useState({ username: '', password: '' });
  const [genootFout, setGenootFout] = useState('');
  // Melding onder het groepsnaamveld, in plaats van een alert-venster: een
  // kind dat een naam moet aanpassen leest liever een regel op de plek waar
  // hij typt dan een pop-up die hij moet wegklikken.
  const [groepFout, setGroepFout] = useState('');
  const [genootBezig, setGenootBezig] = useState(false);
  // Na het kiezen verdeelt het model de rollen over de personages; dat duurt
  // een paar tellen en zonder bericht lijkt de knop niet te werken.
  const [rollenBezig, setRollenBezig] = useState(false);
  // Aanklikken van een kaartje opent de toelichting; kiezen gaat met de knop
  // daaronder. Anders zou je iemand moeten kiezen om te kunnen lezen wie het
  // is, en dat werkt niet als je precies drie van de tien moet uitzoeken.
  const [openPersona, setOpenPersona] = useState(null);
  // De tijdklok voor de eerste reactie van een personage. In een ref, want een
  // herrendering mag hem niet opnieuw zetten en bij het verlaten van het
  // scherm moet hij weg.
  const openingTimer = useRef(null);
  // De hulp die Socrates in de tweede fase geeft. hulpAntwoorden is wat er op
  // het scherm staat; de server bewaart hetzelfde bij de discussie.
  const [hulpAntwoorden, setHulpAntwoorden] = useState([]);
  const [hulpBezig, setHulpBezig] = useState(null);
  const [hulpMelding, setHulpMelding] = useState('');
  const MAX_GENOTEN = 2;
  const [argumentenVoor, setArgumentenVoor] = useState(['']);
  const [argumentenTegen, setArgumentenTegen] = useState(['']);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [reflectieFase, setReflectieFase] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [showExplainPopup, setShowExplainPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const chatEndRef = useRef(null);
  const chatMessagesRef = useRef(null);

  const { verzoek } = maakApi({ apiUrl, token, bijVerlopenToken: onLogout });

  // De leerkracht stelt in hoeveel gesprekspartners een groepje kiest. Zijn er
  // minder personages beschikbaar dan dat, dan zou "kies er precies vier" uit
  // drie een doodloop zijn: de knop wordt nooit actief. Daarom nooit meer
  // vragen dan er te kiezen valt.
  const teKiezen = Math.min(session?.aantal_personas ?? 0, personas.length || (session?.aantal_personas ?? 0));

  // Een persona heeft een portret of niet. Zonder portret blijft het icoon
  // staan, zodat een lege basisset of een weggehaald bestand de chat niet
  // kaal maakt.
  const personaBeeld = (persona, grootte) => (
    <Portret apiUrl={apiUrl} naam={persona?.portret} grootte={grootte} stijl={{ display: 'block' }}
      terugval={<User size={Math.round(grootte * 0.5)} />} />
  );

  const avatars = ['🦊', '🐻', '🦁', '🐼', '🐨', '🐸', '🦉', '🐙', '🦋', '🐝', '🐶', '🐱', '🐰', '🦄', '🐢', '🦈', '🦅', '🦆', '🐧'];

  const avatarNamen = {
    '🦊': 'De Vossen', '🐻': 'De Beren', '🦁': 'De Leeuwen', '🐼': 'De Panda\'s',
    '🐨': 'De Koala\'s', '🐸': 'De Kikkers', '🦉': 'De Uilen', '🐙': 'De Octopussen',
    '🦋': 'De Vlinders', '🐝': 'De Bijen', '🐶': 'De Honden', '🐱': 'De Katten',
    '🐰': 'De Konijnen', '🦄': 'De Eenhoorns', '🐢': 'De Schildpadden', '🦈': 'De Haaien',
    '🦅': 'De Adelaars', '🦆': 'De Eenden', '🐧': 'De Pinguïns'
  };

  const selectAvatar = (avatar) => {
    setGroepjeAvatar(avatar);
    // Naam altijd mee laten veranderen met het icoon
    if (avatarNamen[avatar]) {
      setGroepjeNaam(avatarNamen[avatar]);
    }
  };

  // Eén keer bij het openen; fetchActiveSession is een gewone functie in de
  // component en hoort daarom niet in de lijst.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchActiveSession(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);
  useEffect(() => {
    if (!timerStarted || timeLeft === null) return;
    if (timeLeft <= 0) {
      if (!reflectieFase) {
        // Start reflectiefase: gebruik afronding_duur_min van de stelling
        setReflectieFase(true);
        const afrondingMinuten = session?.afronding_duur_min || 5;
        setTimeLeft(afrondingMinuten * 60);
        setActivePersona(discussieleider);
      } else {
        // Reflectiefase voorbij, ga naar argumenten
        setStep('argumenten');
      }
      return;
    }
    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, timerStarted, reflectieFase, discussieleider, session]);

  const fetchActiveSession = async () => {
    try {
      const response = await verzoek('/student/session?student_id=' + user.id);
      const data = await response.json();
      if (data.success && data.data) { setSession(data.data); setStep('welkom'); }
      else { setStep('geen-sessie'); }
    } catch (err) { setError('Kan geen verbinding maken'); setStep('error'); }
  };

  const voegGenootToe = async () => {
    if (!genootForm.username.trim() || !genootForm.password) {
      setGenootFout('Vul de gebruikersnaam en het wachtwoord in.');
      return;
    }
    setGenootBezig(true);
    setGenootFout('');
    try {
      const response = await verzoek('/student/groepslid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.session_id,
          username: genootForm.username.trim(),
          password: genootForm.password
        })
      });
      const data = await response.json();
      if (data.success) {
        setGroepsgenoten([...groepsgenoten, data.data]);
        setGenootForm({ username: '', password: '' });
      } else {
        setGenootFout(data.detail || 'Dat lukte niet.');
      }
    } catch (err) {
      setGenootFout('Geen verbinding. Probeer het nog een keer.');
    } finally {
      setGenootBezig(false);
    }
  };

  const haalGenootWeg = (id) => {
    setGroepsgenoten(groepsgenoten.filter(g => g.id !== id));
    setGenootFout('');
  };

  const createGroepje = async () => {
    setGroepFout('');
    if (!groepjeNaam.trim()) { setGroepFout('Vul eerst een groepsnaam in.'); return; }
    try {
      const params = new URLSearchParams({ session_id: session.session_id, groepje_naam: groepjeNaam, groepje_avatar: groepjeAvatar });
      if (groepsgenoten.length) {
        params.append('bewijzen', groepsgenoten.map(g => g.bewijs).join(','));
      }
      const response = await verzoek('/student/groepje?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) { setDiscussion(data.data); fetchPersonas(); setStep('personas'); }
      else { setGroepFout(data.detail || 'Dat lukte niet. Probeer het nog eens.'); }
    } catch (err) {
      setGroepFout('Geen verbinding. Probeer het nog eens.');
    }
    finally { setRollenBezig(false); }
  };

  useEffect(() => () => clearTimeout(openingTimer.current), []);

  const fetchPersonas = async () => {
    try {
      const response = await verzoek('/student/personas?session_id=' + session.session_id);
      const data = await response.json();
      if (data.success) setPersonas(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchDiscussieleider = async () => {
    try {
      const response = await verzoek('/student/discussieleider');
      const data = await response.json();
      if (data.success) setDiscussieleider(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const togglePersona = (personaId) => {
    if (selectedPersonas.includes(personaId)) setSelectedPersonas(selectedPersonas.filter(id => id !== personaId));
    else if (selectedPersonas.length < teKiezen) setSelectedPersonas([...selectedPersonas, personaId]);
  };

  const startChat = async () => {
    if (selectedPersonas.length !== teKiezen) { alert('Kies precies ' + teKiezen + ' gesprekspartners'); return; }
    setRollenBezig(true);
    try {
      const personaIdsString = selectedPersonas.join(',');
      const params = new URLSearchParams({ discussion_id: discussion.discussion_id, persona_ids: personaIdsString });
      const response = await verzoek('/student/personas/select?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setDiscussion({ ...discussion, personas: data.data.personas });
        await fetchDiscussieleider();
        setTimeout(() => startDiscussie(), 500);
        setStep('chat');
        // Start de timer - gebruik chat_duur_min van de stelling
        if (session.chat_duur_min) {
          setTimeLeft(session.chat_duur_min * 60);
          setTimerStarted(true);
        }
      }
    } catch (err) { console.error('Fout:', err); }
  };

  // Een willekeurig personage geeft ongevraagd de eerste reactie. De backend
  // doet dit maar een keer per discussie, dus een tweede aanroep is geen ramp;
  // lukt het niet, dan blijft het bij de opening van Socrates en kunnen de
  // leerlingen zelf beginnen.
  const haalOpeningOp = async () => {
    try {
      const params = new URLSearchParams({ discussion_id: discussion.discussion_id });
      const response = await verzoek('/student/persona/opening?' + params, { method: 'POST' });
      const data = await response.json();
      if (!data.success || !data.data) return;
      setChatMessages(prev => [...prev, { type: 'persona', text: data.data.bericht, from: data.data.persona.naam }]);
      // Het invoerveld richten op wie er net sprak, maar alleen als de
      // leerlingen zelf nog niemand hadden aangeklikt. Het personage komt uit
      // het antwoord en niet uit discussion.personas: die lijst is in deze
      // functie de lijst van het moment waarop de tijdklok werd gezet.
      setActivePersona(prev => prev || data.data.persona);
    } catch (err) { console.error('Fout:', err); }
  };

  const startDiscussie = async () => {
    setLoading(true);
    try {
      // Gebruik is_start=true om de socrates_start prompt uit de database te gebruiken
      const params = new URLSearchParams({
        discussion_id: discussion.discussion_id,
        context: 'Start discussie',
        is_start: 'true'
      });
      const response = await verzoek('/student/discussieleider/bericht?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setChatMessages([{ type: 'discussieleider', text: data.data.response.bericht, from: 'Socrates' }]);
        clearTimeout(openingTimer.current);
        openingTimer.current = setTimeout(haalOpeningOp, OPENING_NA_MS);
      }
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !activePersona || loading) return;
    const userMessage = { type: 'user', text: inputMessage, to: activePersona.naam };
    setChatMessages([...chatMessages, userMessage]);
    setInputMessage('');
    setLoading(true);
    try {
      let response, data;
      if (activePersona.id === discussieleider?.id) {
        // Bericht naar discussieleider. De eigen tekst gaat apart mee, zodat
        // het taalfilter op de server alleen daarnaar kijkt en niet naar de
        // berichten van de personages die in de context staan.
        const recentMessages = chatMessages.slice(-5).map(m => m.from + ': ' + m.text).join('\n');
        const context = 'Recente berichten:\n' + recentMessages + '\n\nLeerling vraagt: ' + inputMessage;
        const params = new URLSearchParams({ discussion_id: discussion.discussion_id, context: context, bericht: inputMessage });
        response = await verzoek('/student/discussieleider/bericht?' + params, { method: 'POST' });
        data = await response.json();
        if (data.success) {
          setChatMessages(prev => [...prev, {
            type: data.data.tegengehouden ? 'taalcorrectie' : 'discussieleider',
            text: data.data.response.bericht, from: 'Socrates'
          }]);
        }
      } else {
        // Bericht naar normale persona
        const params = new URLSearchParams({ discussion_id: discussion.discussion_id, persona_id: activePersona.id, bericht: inputMessage });
        response = await verzoek('/student/chat?' + params, { method: 'POST' });
        data = await response.json();
        if (data.success) {
          setChatMessages(prev => [...prev, {
            type: data.data.tegengehouden ? 'taalcorrectie' : 'persona',
            text: data.data.response.bericht, from: data.data.response.van
          }]);
        }
      }

      // Is het bericht tegengehouden, dan heeft de server het niet in het
      // gesprek gezet. Haal het dan ook uit het scherm en zet de tekst terug
      // in het invoerveld, zodat de leerling hem kan herschrijven.
      if (data?.data?.tegengehouden) {
        setChatMessages(prev => prev.filter(m => m !== userMessage));
        setInputMessage(userMessage.text);
      }
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  // Text selectie voor uitleg
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();

    if (text && text.length > 0 && text.length < 200) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Controleer of selectie binnen chat messages is
      if (chatMessagesRef.current && chatMessagesRef.current.contains(range.commonAncestorContainer)) {
        setSelectedText(text);
        setPopupPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 10
        });
        setShowExplainPopup(true);
      }
    } else {
      setShowExplainPopup(false);
      setSelectedText('');
    }
  };

  const askForExplanation = async () => {
    if (!selectedText || loading) return;

    setShowExplainPopup(false);
    setLoading(true);

    try {
      const params = new URLSearchParams({
        discussion_id: discussion.discussion_id,
        selected_text: selectedText
      });
      const response = await verzoek('/student/uitleg?' + params, { method: 'POST' });
      const data = await response.json();

      if (data.success) {
        setChatMessages(prev => [...prev, {
          type: 'uitleg',
          text: data.data.response.bericht,
          from: 'Socrates'
        }]);
      }
    } catch (err) {
      console.error('Fout bij uitleg:', err);
    } finally {
      setLoading(false);
      setSelectedText('');
      window.getSelection().removeAllRanges();
    }
  };

  // Sluit popup bij klik buiten
  useEffect(() => {
    const handleClickOutside = () => {
      if (showExplainPopup) {
        setShowExplainPopup(false);
        setSelectedText('');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExplainPopup]);

  const addArgument = (type) => {
    if (type === 'voor') setArgumentenVoor([...argumentenVoor, '']);
    else setArgumentenTegen([...argumentenTegen, '']);
  };

  const updateArgument = (type, index, value) => {
    if (type === 'voor') { const n = [...argumentenVoor]; n[index] = value; setArgumentenVoor(n); }
    else { const n = [...argumentenTegen]; n[index] = value; setArgumentenTegen(n); }
  };

  const removeArgument = (type, index) => {
    if (type === 'voor') setArgumentenVoor(argumentenVoor.filter((_, i) => i !== index));
    else setArgumentenTegen(argumentenTegen.filter((_, i) => i !== index));
  };

  // De vier hulpvormen. verhelder en vereenvoudig werken op wat de leerling
  // in het gesprek heeft geselecteerd; hints en kritiek op wat er in het
  // formulier staat.
  const HULP_LABELS = {
    verhelder: 'Uitleg',
    vereenvoudig: 'Korter gezegd',
    hints: 'Tip',
    kritiek: 'Vragen bij je argument'
  };

  const geselecteerdeTekst = () => {
    try { return (window.getSelection()?.toString() || '').trim(); }
    catch (err) { return ''; }
  };

  const vraagHulp = async (soort, extra = {}) => {
    setHulpMelding('');
    let tekst = extra.tekst || '';
    if ((soort === 'verhelder' || soort === 'vereenvoudig') && !tekst) {
      tekst = geselecteerdeTekst();
      if (!tekst) {
        setHulpMelding('Selecteer eerst een stukje tekst in het gesprek hieronder.');
        return;
      }
    }
    setHulpBezig(soort);
    try {
      const params = new URLSearchParams({
        discussion_id: discussion.discussion_id,
        soort,
        tekst,
        kant: extra.kant || '',
        argumenten_voor: argumentenVoor.filter(a => a.trim()).join('\n'),
        argumenten_tegen: argumentenTegen.filter(a => a.trim()).join('\n')
      });
      const response = await verzoek('/student/socrates/hulp?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setHulpAntwoorden(prev => [{
          soort,
          label: HULP_LABELS[soort] || 'Socrates',
          overTekst: tekst,
          antwoord: data.data.antwoord
        }, ...prev]);
      } else {
        setHulpMelding(data.detail || 'Dat lukte niet. Probeer het nog eens.');
      }
    } catch (err) {
      setHulpMelding('Geen verbinding met Socrates. Probeer het nog eens.');
    } finally {
      setHulpBezig(null);
    }
  };

  const vraagBronnen = async () => {
    setHulpMelding('');
    setHulpBezig('bronnen');
    try {
      const params = new URLSearchParams({ discussion_id: discussion.discussion_id });
      const response = await verzoek('/student/bronnen?' + params);
      const data = await response.json();
      if (data.success) {
        setHulpAntwoorden(prev => [{ soort: 'bronnen', label: 'De bronnen', bronnen: data.data }, ...prev]);
      } else {
        setHulpMelding('De bronnen konden niet worden opgehaald.');
      }
    } catch (err) {
      setHulpMelding('Geen verbinding. Probeer het nog eens.');
    } finally {
      setHulpBezig(null);
    }
  };

  const saveArgumenten = async () => {
    const voorFiltered = argumentenVoor.filter(a => a.trim());
    const tegenFiltered = argumentenTegen.filter(a => a.trim());
    if (voorFiltered.length === 0 && tegenFiltered.length === 0) { alert('Voeg minimaal één argument toe'); return; }
    try {
      const params = new URLSearchParams({ discussion_id: discussion.discussion_id, argumenten_voor: voorFiltered.join('\n'), argumenten_tegen: tegenFiltered.join('\n') });
      const response = await verzoek('/student/argumenten?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) setStep('afronden');
    } catch (err) { console.error('Fout:', err); }
  };

  const afronden = async () => {
    try {
      const params = new URLSearchParams({ discussion_id: discussion.discussion_id });
      const response = await verzoek('/student/afronden?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) setStep('klaar');
    } catch (err) { console.error('Fout:', err); }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
       <img src="/socrates_logo.png" alt="Socrates" style={{ height: '125px' }} />
        <div style={styles.userInfo}>
          <span>{user.naam}</span>
        <button onClick={onLogout} style={styles.logoutBtn}><LogOut size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Uitloggen</button>
        </div>
      </div>

      <div style={styles.content}>
        {step === 'loading' && <div style={styles.card}><p>Laden...</p></div>}

        {step === 'error' && (
          <div style={styles.card}>
            <h2>😕 Er ging iets mis</h2>
            <p>{error}</p>
            <button onClick={fetchActiveSession} style={styles.btn}>Opnieuw proberen</button>
          </div>
        )}

        {step === 'geen-sessie' && (
          <div style={styles.card}>
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>📭</span>
              <h2>Geen actieve opdracht</h2>
              <p>Je leraar heeft nog geen discussie-opdracht voor je klaargezet.</p>
            </div>
          </div>
        )}


	{step === 'welkom' && session && (
  	  <div style={styles.card}>
    		<div style={styles.welcomeHeader}>
		<h1>Welkom bij Socrates!</h1>
      		<p style={styles.subtitle}>Leren argumenteren</p>
    	 </div>

    		<div style={styles.stellingCard}>
      		<h3 style={styles.stellingLabel}>De stelling van vandaag:</h3>
      		<p style={styles.stellingTekst}>{session.statement?.titel}</p>
    	</div>

    		<div style={styles.infoBox}>
          <div style={styles.infoItem}>
                  <span>•</span>
                  <span>Je gaat discussiëren met {session.aantal_personas} personen</span>
                </div>
                <div style={styles.infoItem}>
                  <span>•</span>
                  <span>Socrates staat klaar voor vragen over de discussie</span>
                </div>
                <div style={styles.infoItem}>
                  <span>•</span>
                  <span>Verzamel argumenten VOOR en TEGEN de stelling</span>
      	</div>
    	</div> 

    <button onClick={() => setStep('groepje')} style={styles.startBtn}>
        Start de discussie
    	</button>
  	</div>
	)}


        {step === 'groepje' && (
          <div style={styles.card}>
            <div style={styles.stepHeader}>
              <span style={styles.stepNumber}>1</span>
              <div><h2 style={styles.stepTitle}>Maak je groepje</h2><p style={styles.stepSubtitle}>Wie doen er mee, en hoe heet jullie groepje?</p></div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Wie werken er met je mee?</label>
              <p style={styles.uitleg}>
                Jullie werken samen onder het account van {user.naam}. Je groepsgenoten
                hoeven niet apart in te loggen — ze typen hier alleen even hun eigen
                gebruikersnaam en wachtwoord, zodat hun naam bij het groepje komt te staan.
                Werk je alleen? Laat dit dan leeg.
              </p>

              <div style={styles.genootRij}>
                <span style={styles.genootZelf}>{user.naam} (jij)</span>
                {groepsgenoten.map(g => (
                  <span key={g.id} style={styles.genoot}>
                    {g.naam}
                    <button onClick={() => haalGenootWeg(g.id)} style={styles.genootWeg} title="Haal weg">×</button>
                  </span>
                ))}
              </div>

              {groepsgenoten.length < MAX_GENOTEN ? (
                <div style={styles.genootForm}>
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="Gebruikersnaam groepsgenoot"
                    value={genootForm.username}
                    onChange={(e) => setGenootForm({ ...genootForm, username: e.target.value })}
                    style={styles.input}
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder="Wachtwoord"
                    value={genootForm.password}
                    onChange={(e) => setGenootForm({ ...genootForm, password: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') voegGenootToe(); }}
                    style={styles.input}
                  />
                  <button onClick={voegGenootToe} disabled={genootBezig} style={styles.btnSecondary}>
                    {genootBezig ? 'Even kijken…' : 'Erbij zetten'}
                  </button>
                </div>
              ) : (
                <p style={styles.uitleg}>Een groepje is maximaal drie leerlingen. Vol.</p>
              )}
              {genootFout && <p style={styles.genootFout}>{genootFout}</p>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Groepsnaam</label>
              <input type="text" value={groepjeNaam}
                onChange={(e) => { setGroepjeNaam(e.target.value); setGroepFout(''); }}
                placeholder="Bijv. De Denkers" style={styles.input} maxLength={30} />
              {groepFout && <p style={styles.genootFout}>{groepFout}</p>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Kies een avatar</label>
              <div style={styles.avatarGrid}>
                {avatars.map(avatar => (
                  <button key={avatar} onClick={() => selectAvatar(avatar)} style={groepjeAvatar === avatar ? styles.avatarBtnActive : styles.avatarBtn}>{avatar}</button>
                ))}
              </div>
            </div>
            <div style={styles.preview}><span style={styles.previewAvatar}>{groepjeAvatar}</span><span style={styles.previewName}>{groepjeNaam || 'Jouw groepsnaam'}</span></div>
            <div style={styles.buttonRow}>
              <button onClick={() => setStep('welkom')} style={styles.btnSecondary}><ChevronLeft size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Terug</button>
              <button onClick={createGroepje} style={styles.btn}>Verder<ChevronRight size={16} style={{ marginLeft: '4px', verticalAlign: 'middle' }} /></button>
            </div>
          </div>
        )}

        {step === 'personas' && (
          <div style={styles.card}>
            <div style={styles.stepHeader}>
              <span style={styles.stepNumber}>2</span>
              <div><h2 style={styles.stepTitle}>Kies je gesprekspartners</h2><p style={styles.stepSubtitle}>Kies er {teKiezen} uit deze {personas.length}</p></div>
            </div>
            <div style={styles.groupBadge}>
                <span>{groepjeAvatar} {groepjeNaam}</span>
                {discussion?.leden?.length > 1 && (
                  <span style={styles.groupLeden}>{discussion.leden.map(l => l.naam).join(' • ')}</span>
                )}
              </div>
            <div style={styles.personaCounter}>{selectedPersonas.length} / {teKiezen} gekozen</div>
            <p style={styles.uitleg}>Tik op iemand om te lezen wie het is.</p>
            <div style={styles.personaGrid}>
              {personas.map(persona => {
                const gekozen = selectedPersonas.includes(persona.id);
                const staatOpen = openPersona === persona.id;
                return (
                  <button
                    key={persona.id}
                    onClick={() => setOpenPersona(staatOpen ? null : persona.id)}
                    style={gekozen ? styles.personaTegelGekozen : staatOpen ? styles.personaTegelOpen : styles.personaTegel}
                  >
                    <div style={styles.personaAvatar}>{personaBeeld(persona, 72)}</div>
                    <div style={styles.personaNaam}>{persona.naam}</div>
                    {gekozen && <span style={styles.checkmark}><Check size={18} /></span>}
                  </button>
                );
              })}
            </div>

            {openPersona && (() => {
              const persona = personas.find(p => p.id === openPersona);
              if (!persona) return null;
              const gekozen = selectedPersonas.includes(persona.id);
              const vol = selectedPersonas.length >= teKiezen;
              return (
                <div style={styles.persoonBlok}>
                  <div style={styles.persoonBeeld}>{personaBeeld(persona, 96)}</div>
                  <div style={styles.persoonTekst}>
                    <div style={styles.persoonNaam}>{persona.naam}</div>
                    <p style={styles.persoonToelichting}>{persona.toelichting}</p>
                    <button
                      onClick={() => togglePersona(persona.id)}
                      disabled={!gekozen && vol}
                      style={gekozen ? styles.btnSecondary : (vol ? styles.btnDisabled : styles.btn)}
                    >
                      {gekozen ? 'Haal weg' : vol ? 'Je hebt er al ' + teKiezen : 'Kies ' + persona.naam.split(' ')[0]}
                    </button>
                  </div>
                </div>
              );
            })()}
            <div style={styles.buttonRow}>
              <button onClick={() => setStep('groepje')} style={styles.btnSecondary}><ChevronLeft size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Terug</button>
              <button onClick={startChat} disabled={rollenBezig}
                style={selectedPersonas.length === teKiezen && !rollenBezig ? styles.btn : styles.btnDisabled}>
                {rollenBezig ? 'De rollen worden verdeeld…' : 'Start de chat →'}
              </button>
            </div>
          </div>
        )}

        {step === 'chat' && (
            <div style={styles.chatContainer}>
              <div style={styles.chatHeader}>
                <div style={styles.groupBadge}>
                <span>{groepjeAvatar} {groepjeNaam}</span>
                {discussion?.leden?.length > 1 && (
                  <span style={styles.groupLeden}>{discussion.leden.map(l => l.naam).join(' • ')}</span>
                )}
              </div>
                {timeLeft !== null && (
                  <div style={timeLeft < 60 ? styles.timerWarning : styles.timer}>
                    {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                  </div>
                )}
                <div style={styles.stellingMini}>De stelling: {session.statement?.titel}</div>
                <button onClick={() => setStep('argumenten')} style={styles.btnSmall}>Argumenten</button>
              </div>
            <div style={styles.chatLayout}>
              <div style={styles.personaSidebar}>
                <h3 style={styles.sidebarTitle}>Discussieleider</h3>
                {discussieleider && (
                  <div onClick={() => setActivePersona(discussieleider)} style={activePersona?.id === discussieleider.id ? styles.sidebarPersonaActive : styles.sidebarPersona}>
                    <span style={styles.sidebarAvatar}>{personaBeeld(discussieleider, 36)}</span>
                    <div>
                      <div style={styles.sidebarNaam}>{discussieleider.naam}</div>
                      {/* Zijn profiel staat er bewust bij: Socrates kondigt in zijn
                          openingsbericht aan dat je hem om hulp kunt vragen, en dan
                          moet in de lijst te zien zijn dat hij daarvoor is. */}
                      <div style={styles.sidebarOmschrijving}>{discussieleider.toelichting || 'Vraag mij om uitleg'}</div>
                    </div>
                  </div>
                )}
                {!reflectieFase && (
                  <>
                    <h3 style={styles.sidebarTitle}>Gesprekspartners</h3>
                    {discussion.personas?.map(p => (
                      <div key={p.id} onClick={() => setActivePersona(p)} style={activePersona?.id === p.id ? styles.sidebarPersonaActive : styles.sidebarPersona}>
                        <span style={styles.sidebarAvatar}>{personaBeeld(p, 36)}</span>
                        <div>
                          <div style={styles.sidebarNaam}>{p.naam}</div>
                          <div style={styles.sidebarOmschrijving}>{p.toelichting}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {reflectieFase && (
                  <div style={styles.reflectieNotice}>
                    <p>De discussietijd is om!</p>
                    <p>Bespreek met Socrates welke argumenten je hebt gevonden.</p>
                  </div>
                )}                
              </div>

              <div style={styles.chatMain}>
                <div style={styles.chatMessages} ref={chatMessagesRef} onMouseUp={handleTextSelection}>
                  <div style={styles.selectHint}>
                    <BookOpen size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Tip: Selecteer tekst om uitleg te vragen aan Socrates
                  </div>
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} style={
                      msg.type === 'user' ? styles.userMessage :
                      msg.type === 'uitleg' ? styles.uitlegMessage :
                      msg.type === 'taalcorrectie' ? styles.correctieMessage :
                      styles.personaMessage
                    }>
                      {msg.type !== 'user' && <span style={msg.type === 'uitleg' ? styles.messageFromUitleg : styles.messageFrom}>{msg.from}</span>}
                      <p style={styles.messageText}>{msg.text}</p>
                      {msg.type === 'user' && <span style={styles.messageTo}>→ {msg.to}</span>}
                    </div>
                  ))}
                  {loading && <div style={styles.typing}>{activePersona?.naam || 'Socrates'} is aan het typen...</div>}
                  <div ref={chatEndRef} />
                </div>

                {/* Uitleg popup */}
                {showExplainPopup && (
                  <div
                    style={{
                      ...styles.explainPopup,
                      left: popupPosition.x,
                      top: popupPosition.y
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button onClick={askForExplanation} style={styles.explainBtn}>
                      <BookOpen size={14} style={{ marginRight: '6px' }} />
                      Vraag uitleg aan Socrates
                    </button>
                  </div>
                )}

                <div style={styles.chatInput}>
                  <div style={styles.inputLabel}>
                    {activePersona && <span style={styles.inputLabelPortret}>{personaBeeld(activePersona, 28)}</span>}
                    Vraag aan: <strong>{activePersona?.naam || 'Kies een persoon'}</strong>
                  </div>
                  <div style={styles.inputRow}>
                    <textarea value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="Typ je bericht..." style={styles.textarea} rows={2} disabled={!activePersona} />
                    <button onClick={sendMessage} disabled={loading || !inputMessage.trim() || !activePersona} style={styles.sendBtn}><Send size={20} /></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'argumenten' && (
          <div style={styles.card}>
            <div style={styles.argumentenHeader}>
              <div style={styles.groupBadge}>
                <span>{groepjeAvatar} {groepjeNaam}</span>
                {discussion?.leden?.length > 1 && (
                  <span style={styles.groupLeden}>{discussion.leden.map(l => l.naam).join(' • ')}</span>
                )}
              </div>
              {timeLeft !== null && timeLeft > 0 && (
                <div style={timeLeft < 60 ? styles.timerWarning : styles.timer}>
                  {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
            <div style={styles.stepHeader}>
              <span style={styles.stepNumber}>4</span>
              <div><h2 style={styles.stepTitle}>Verzamel je argumenten</h2><p style={styles.stepSubtitle}>Wat heb je geleerd?</p></div>
            </div>
            <div style={styles.stellingCard}>
              <h3 style={styles.stellingLabel}>De stelling:</h3>
              <p style={styles.stellingTekst}>{session.statement?.titel}</p>
            </div>
            <div style={styles.argumentenGrid}>
              <div style={styles.argumentenColumn}>
                <h3 style={styles.argumentenTitle}>✅ VOOR</h3>
                {argumentenVoor.map((arg, idx) => (
                  <div key={idx} style={styles.argumentRow}>
                    <input type="text" value={arg} onChange={(e) => updateArgument('voor', idx, e.target.value)} placeholder="Typ een argument..." style={styles.argumentInput} />
                    <button
                      onClick={() => vraagHulp('kritiek', { tekst: arg.trim(), kant: 'voor' })}
                      disabled={!arg.trim() || hulpBezig === 'kritiek'}
                      title="Laat Socrates vragen stellen bij dit argument"
                      style={arg.trim() ? styles.kritiekBtn : styles.kritiekBtnUit}
                    >?</button>
                    {argumentenVoor.length > 1 && <button onClick={() => removeArgument('voor', idx)} style={styles.removeBtn}><X size={16} /></button>}
                  </div>
                ))}
                <button onClick={() => addArgument('voor')} style={styles.addArgBtn}><Plus size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Toevoegen</button>
              </div>
              <div style={styles.argumentenColumn}>
                <h3 style={styles.argumentenTitleTegen}>❌ TEGEN</h3>
                {argumentenTegen.map((arg, idx) => (
                  <div key={idx} style={styles.argumentRow}>
                    <input type="text" value={arg} onChange={(e) => updateArgument('tegen', idx, e.target.value)} placeholder="Typ een argument..." style={styles.argumentInput} />
                    <button
                      onClick={() => vraagHulp('kritiek', { tekst: arg.trim(), kant: 'tegen' })}
                      disabled={!arg.trim() || hulpBezig === 'kritiek'}
                      title="Laat Socrates vragen stellen bij dit argument"
                      style={arg.trim() ? styles.kritiekBtn : styles.kritiekBtnUit}
                    >?</button>
                    {argumentenTegen.length > 1 && <button onClick={() => removeArgument('tegen', idx)} style={styles.removeBtn}><X size={16} /></button>}
                  </div>
                ))}
                <button onClick={() => addArgument('tegen')} style={styles.addArgBtn}><Plus size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Toevoegen</button>
              </div>
            </div>
            <div style={styles.hulpPaneel}>
              <h3 style={styles.hulpKop}>Hulp van Socrates</h3>
              <p style={styles.uitleg}>
                Socrates geeft je geen argumenten — die bedenk je zelf. Hij legt wel uit
                wat er is gezegd, en stelt vragen bij wat je hebt opgeschreven.
              </p>
              <div style={styles.hulpKnoppen}>
                <button onClick={() => vraagHulp('verhelder')} disabled={!!hulpBezig} style={styles.hulpBtn}>
                  {hulpBezig === 'verhelder' ? 'Even kijken…' : 'Leg uit wat er staat'}
                </button>
                <button onClick={() => vraagHulp('vereenvoudig')} disabled={!!hulpBezig} style={styles.hulpBtn}>
                  {hulpBezig === 'vereenvoudig' ? 'Even kijken…' : 'Zeg het korter'}
                </button>
                <button onClick={() => vraagHulp('hints')} disabled={!!hulpBezig} style={styles.hulpBtn}>
                  {hulpBezig === 'hints' ? 'Even kijken…' : 'Geef me een tip'}
                </button>
                <button onClick={vraagBronnen} disabled={!!hulpBezig} style={styles.hulpBtn}>
                  {hulpBezig === 'bronnen' ? 'Even kijken…' : 'Welke bronnen?'}
                </button>
              </div>
              {hulpMelding && <p style={styles.hulpMelding}>{hulpMelding}</p>}

              {hulpAntwoorden.map((h, idx) => (
                <div key={idx} style={styles.hulpAntwoord}>
                  <div style={styles.hulpLabel}>{h.label}</div>
                  {h.overTekst && <div style={styles.hulpOver}>over: “{h.overTekst}”</div>}
                  {h.soort === 'bronnen' ? (
                    h.bronnen.length === 0 ? (
                      <p style={styles.hulpTekst}>Bij deze stelling zijn geen bronnen opgegeven.</p>
                    ) : (
                      <>
                        <p style={styles.hulpTekst}>
                          Hier komt de kennis van de deelnemers uit. Je ziet alleen de titels —
                          wat vind je zelf: is dit te vertrouwen?
                        </p>
                        <ul style={styles.bronnenLijst}>
                          {h.bronnen.map((b, i) => (
                            <li key={i} style={styles.bronRegel}>
                              <span>{b.titel}</span>
                              {b.kant && <span style={styles.bronKant}>{b.kant}</span>}
                            </li>
                          ))}
                        </ul>
                      </>
                    )
                  ) : (
                    <p style={styles.hulpTekst}>{h.antwoord}</p>
                  )}
                </div>
              ))}
            </div>

            <div style={styles.gesprekBlok}>
              <h3 style={styles.hulpKop}>Het gesprek</h3>
              <p style={styles.uitleg}>
                Selecteer hier een stukje tekst en gebruik dan “Leg uit wat er staat”
                of “Zeg het korter”.
              </p>
              <div style={styles.gesprekLog}>
                {chatMessages.length === 0 ? (
                  <p style={styles.hulpTekst}>Er is nog geen gesprek.</p>
                ) : chatMessages.map((msg, idx) => (
                  <div key={idx} style={styles.gesprekRegel}>
                    <span style={styles.gesprekVan}>{msg.type === 'user' ? 'Jullie' : msg.from}</span>
                    <span>{msg.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.buttonRow}>
              <button onClick={() => setStep('chat')} style={styles.btnSecondary}><ChevronLeft size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Terug naar chat</button>
              {timeLeft !== null && timeLeft > 0 ? (
                <button style={styles.btnDisabled} disabled>Opslaan (wacht op timer)</button>
              ) : (
                <button onClick={saveArgumenten} style={styles.btn}>Opslaan →</button>
              )}
            </div>
          </div>
        )}

        {step === 'afronden' && (
          <div style={styles.card}>
            <div style={styles.stepHeader}>
              <span style={styles.stepNumber}>5</span>
              <div><h2 style={styles.stepTitle}>Bijna klaar!</h2><p style={styles.stepSubtitle}>Check je argumenten</p></div>
            </div>
            <div style={styles.summaryBox}>
              <h3>✅ VOOR:</h3>
              <ul>{argumentenVoor.filter(a => a.trim()).map((a, i) => <li key={i}>{a}</li>)}</ul>
              <h3>❌ TEGEN:</h3>
              <ul>{argumentenTegen.filter(a => a.trim()).map((a, i) => <li key={i}>{a}</li>)}</ul>
            </div>
            <div style={styles.buttonRow}>
              <button onClick={() => setStep('argumenten')} style={styles.btnSecondary}>← Aanpassen</button>
              <button onClick={afronden} style={styles.btnSuccess}><Check size={20} /> Afronden</button>
            </div>
          </div>
        )}

        {step === 'klaar' && (
          <div style={styles.card}>
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>🎉</span>
              <h2>Goed gedaan!</h2>
              <p>Je hebt de discussie succesvol afgerond.</p>
              <p style={styles.hint}>Je leraar kan nu je argumenten bekijken.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  ...gedeeld,
  header: { background: 'white', padding: '16px 56px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: kleur.inkt },
  userInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  content: { padding: '16px 24px', maxWidth: '1000px', margin: '0 auto' },
  card: { background: 'white', padding: '32px' },
  emptyState: { textAlign: 'center', padding: '20px' },
  emptyIcon: { fontSize: '64px', display: 'block', marginBottom: '16px' },
  hint: { color: kleur.stiller, fontSize: '14px', marginTop: '16px' },
  welcomeHeader: { textAlign: 'center', marginBottom: '32px' },
  subtitle: { color: kleur.stil },
  stellingCard: { background: kleur.vlak, borderRadius: '12px', padding: '20px', marginBottom: '24px', borderLeft: `4px solid ${kleur.accent}` },
  stellingLabel: { fontSize: '14px', color: kleur.stil, marginBottom: '8px' },
  stellingTekst: { fontSize: '20px', fontWeight: 'bold', color: kleur.inkt, margin: 0 },
  infoBox: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' },
  infoItem: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px', color: kleur.tekstZacht },
  startBtn: { width: 'auto', padding: '16px 48px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', display: 'block', margin: '0 auto' },
  btn: { padding: '12px 24px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' },
  btnSecondary: { padding: '12px 24px', background: kleur.lijn, color: kleur.tekst, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' },
  btnDisabled: { padding: '12px 24px', background: kleur.lijnUit, color: kleur.grijs, border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '16px' },
  btnSmall: { padding: '8px 16px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  btnSuccess: { padding: '12px 24px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' },
  stepHeader: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
  stepNumber: { width: '40px', height: '40px', background: kleur.accent, color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold' },
  stepTitle: { margin: 0, fontSize: '20px' },
  stepSubtitle: { margin: 0, color: kleur.stil, fontSize: '14px' },
  formGroup: { marginBottom: '20px' },
  label: { display: 'block', marginBottom: '8px', fontWeight: 'bold', color: kleur.inkt },
  input: { width: '100%', padding: '14px', border: `2px solid ${kleur.lijn}`, borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' },
  hulpPaneel: { background: kleur.vlakKoel, border: `1px solid ${kleur.lijn}`, borderRadius: '14px', padding: '16px', marginTop: '20px' },
  hulpKop: { margin: '0 0 6px', fontSize: '16px' },
  hulpKnoppen: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  hulpBtn: { padding: '10px 14px', border: `2px solid ${kleur.accent}`, background: 'white', color: kleur.accentTekst, borderRadius: '10px', cursor: 'pointer', fontSize: '14px', font: 'inherit' },
  hulpMelding: { color: kleur.amberTekst, background: kleur.amber, padding: '8px 10px', borderRadius: '8px', fontSize: '14px', margin: '10px 0 0' },
  hulpAntwoord: { background: 'white', border: `1px solid ${kleur.lijnLichter}`, borderRadius: '10px', padding: '12px 14px', marginTop: '12px' },
  hulpLabel: { fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em', color: kleur.groenZacht, fontWeight: 'bold', marginBottom: '4px' },
  hulpOver: { fontSize: '13px', color: kleur.tekstStil, fontStyle: 'italic', marginBottom: '6px' },
  hulpTekst: { margin: 0, fontSize: '15px', lineHeight: 1.55, whiteSpace: 'pre-wrap' },
  bronnenLijst: { listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' },
  bronRegel: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline', fontSize: '14px' },
  bronKant: { fontSize: '12px', color: kleur.tekstGrijs, background: kleur.vlakBron, padding: '2px 8px', borderRadius: '10px' },
  gesprekBlok: { marginTop: '20px' },
  gesprekLog: { maxHeight: '260px', overflowY: 'auto', background: 'white', border: `1px solid ${kleur.lijn}`, borderRadius: '12px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' },
  gesprekRegel: { fontSize: '14px', lineHeight: 1.5 },
  kritiekBtn: { width: '30px', height: '30px', flexShrink: 0, border: `2px solid ${kleur.accent}`, background: 'white', color: kleur.accentTekst, borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', font: 'inherit' },
  kritiekBtnUit: { width: '30px', height: '30px', flexShrink: 0, border: `2px solid ${kleur.lijn}`, background: kleur.vlakWarm, color: kleur.grijsLicht, borderRadius: '50%', cursor: 'default', fontWeight: 'bold', font: 'inherit' },
  groupLeden: { fontSize: '13px', opacity: 0.8 },
  uitleg: { fontSize: '14px', color: kleur.tekstGrijs, lineHeight: 1.5, margin: '0 0 12px' },
  genootRij: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' },
  genootZelf: { background: kleur.accentLicht, color: kleur.accentTekst, padding: '6px 12px', borderRadius: '14px', fontSize: '14px' },
  genoot: { background: kleur.groenLicht, color: kleur.groenTekst, padding: '6px 6px 6px 12px', borderRadius: '14px', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px' },
  genootWeg: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '16px', lineHeight: 1, color: kleur.groenTekst, padding: '0 4px' },
  genootForm: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },
  genootFout: { color: kleur.roodDonker, fontSize: '14px', margin: '8px 0 0' },
  avatarGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' },
  avatarBtn: { padding: '12px', fontSize: '28px', background: kleur.vlakGrijs, border: '2px solid transparent', borderRadius: '12px', cursor: 'pointer' },
  avatarBtnActive: { padding: '12px', fontSize: '28px', background: kleur.accentLicht, border: `2px solid ${kleur.accent}`, borderRadius: '12px', cursor: 'pointer' },
  preview: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: kleur.vlak, borderRadius: '12px', marginBottom: '24px' },
  previewAvatar: { fontSize: '32px' },
  previewName: { fontSize: '18px', fontWeight: 'bold', color: kleur.inkt },
  buttonRow: { display: 'flex', justifyContent: 'space-between', gap: '12px' },
  groupBadge: { display: 'inline-flex', flexDirection: 'column', gap: '2px', padding: '8px 16px', background: kleur.accentLicht, borderRadius: '20px', fontSize: '14px' },
  personaCounter: { textAlign: 'center', fontSize: '16px', color: kleur.inkt, fontWeight: 'bold', marginBottom: '16px' },
  personaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px', marginBottom: '8px' },
  personaTegel: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '14px 10px', background: 'white', border: `2px solid ${kleur.lijnTegel}`, borderRadius: '14px', cursor: 'pointer', position: 'relative', font: 'inherit', textAlign: 'center' },
  personaTegelOpen: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '14px 10px', background: 'white', border: `2px solid ${kleur.accent}`, borderRadius: '14px', cursor: 'pointer', position: 'relative', font: 'inherit', textAlign: 'center' },
  personaTegelGekozen: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '14px 10px', background: kleur.accentLicht, border: `2px solid ${kleur.accent}`, borderRadius: '14px', cursor: 'pointer', position: 'relative', font: 'inherit', textAlign: 'center' },
  persoonBlok: { display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap', background: kleur.vlakKoel, border: `1px solid ${kleur.lijn}`, borderRadius: '14px', padding: '16px', marginTop: '16px' },
  persoonBeeld: { flexShrink: 0 },
  persoonTekst: { flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' },
  persoonNaam: { fontWeight: 'bold', fontSize: '17px' },
  persoonToelichting: { margin: 0, fontSize: '15px', lineHeight: 1.5, color: kleur.tekstDonker },
  personaAvatar: { width: '72px', height: '72px', background: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 },
  inputLabelPortret: { display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' },
  personaNaam: { fontWeight: 'bold', fontSize: '16px', color: kleur.inkt },
  checkmark: { position: 'absolute', top: '6px', right: '6px', background: kleur.accent, color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  chatContainer: { background: 'white', overflow: 'hidden' },
  chatHeader: { padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  stellingMini: { fontSize: '14px', color: kleur.stil, flex: 1, textAlign: 'center' },
  chatLayout: { display: 'grid', gridTemplateColumns: '250px 1fr', minHeight: '500px' },
  personaSidebar: { background: kleur.vlak, padding: '16px', borderRight: `1px solid ${kleur.lijn}` },
  sidebarTitle: { fontSize: '12px', color: kleur.stil, marginBottom: '12px', marginTop: '16px', textTransform: 'uppercase' },
  sidebarPersona: { display: 'flex', gap: '12px', padding: '12px', borderRadius: '8px', cursor: 'pointer', marginBottom: '8px', background: 'white' },
  sidebarPersonaActive: { display: 'flex', gap: '12px', padding: '12px', borderRadius: '8px', cursor: 'pointer', marginBottom: '8px', background: kleur.accent, color: 'white' },
  sidebarAvatar: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', background: 'white', flexShrink: 0 },
  sidebarNaam: { fontWeight: 'bold', fontSize: '14px' },
  sidebarOmschrijving: { fontSize: '11px', opacity: 0.8 },
  chatMain: { display: 'flex', flexDirection: 'column' },
  chatMessages: { flex: 1, padding: '20px', overflowY: 'auto', maxHeight: '400px' },
  userMessage: { background: 'white', color: 'black', padding: '12px 16px', borderRadius: '8px', marginBottom: '12px', maxWidth: '80%', marginLeft: 'auto', border: '1px solid black' },
  personaMessage: { background: 'white', color: 'black', padding: '12px 16px', borderRadius: '8px', marginBottom: '12px', maxWidth: '80%', border: '1px solid black' },
  messageFrom: { fontSize: '12px', fontWeight: 'bold', color: 'black', marginBottom: '4px', display: 'block' },
  messageText: { margin: 0, lineHeight: 1.5 },
  messageTo: { fontSize: '11px', opacity: 0.8, marginTop: '4px', display: 'block', textAlign: 'right' },
  typing: { color: kleur.stil, fontStyle: 'italic', padding: '10px' },
  chatInput: { padding: '16px', borderTop: `1px solid ${kleur.lijn}` },
  inputLabel: { fontSize: '13px', color: kleur.stil, marginBottom: '8px' },
  inputRow: { display: 'flex', gap: '12px' },
  textarea: { flex: 1, padding: '12px', border: `2px solid ${kleur.lijn}`, borderRadius: '8px', fontSize: '14px', resize: 'none' },
  sendBtn: { padding: '12px 20px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px' },
  argumentenHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px 16px', background: kleur.vlak, borderRadius: '8px' },
  argumentenGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' },
  argumentenColumn: { background: kleur.vlak, padding: '20px', borderRadius: '12px' },
  argumentenTitle: { color: 'black', marginBottom: '16px', fontSize: '16px' },
  argumentenTitleTegen: { color: 'black', marginBottom: '16px', fontSize: '16px' },
  argumentRow: { display: 'flex', gap: '8px', marginBottom: '12px' },
  argumentInput: { flex: 1, padding: '10px', border: `2px solid ${kleur.lijn}`, borderRadius: '6px', fontSize: '14px' },
  removeBtn: { padding: '8px 12px', background: kleur.rood, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  addArgBtn: { padding: '8px 16px', background: 'transparent', color: kleur.accent, border: `2px dashed ${kleur.accent}`, borderRadius: '6px', cursor: 'pointer', width: '100%' },
  summaryBox: { background: kleur.vlak, padding: '20px', borderRadius: '12px', marginBottom: '24px' },
  timer: { background: kleur.accent, color: 'white', padding: '8px 16px', borderRadius: '20px', fontSize: '16px', fontWeight: 'bold' },
  timerWarning: { background: kleur.rood, color: 'white', padding: '8px 16px', borderRadius: '20px', fontSize: '16px', fontWeight: 'bold', animation: 'pulse 1s infinite' },
  reflectieNotice: { background: 'white', padding: '16px', borderRadius: '8px', marginTop: '16px', textAlign: 'center', fontSize: '14px', color: 'black' },
  correctieMessage: { alignSelf: 'center', maxWidth: '90%', background: kleur.amber, border: `1px solid ${kleur.amberLijn}`, color: kleur.amberDonker, padding: '12px 16px', borderRadius: '12px' },
  uitlegMessage: { background: kleur.geelLicht, color: 'black', padding: '12px 16px', borderRadius: '8px', marginBottom: '12px', maxWidth: '80%', border: `2px solid ${kleur.geel}`, whiteSpace: 'pre-wrap' },
  messageFromUitleg: { fontSize: '12px', fontWeight: 'bold', color: kleur.geelDonker, marginBottom: '4px', display: 'block' },
  explainPopup: { position: 'fixed', transform: 'translateX(-50%) translateY(-100%)', zIndex: 1000, background: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '4px' },
  explainBtn: { display: 'flex', alignItems: 'center', padding: '8px 16px', background: kleur.geel, color: kleur.tekst, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' },
  selectHint: { textAlign: 'center', padding: '8px 16px', background: kleur.vlakGrijsLicht, borderRadius: '20px', fontSize: '12px', color: kleur.stil, marginBottom: '16px', display: 'inline-block', marginLeft: '50%', transform: 'translateX(-50%)' },
};

export default StudentFlow;
